import { useMemo } from 'react';

import { CareerCategory, CareerPath, Industry } from '@backrs_ui/common-ui';

import {
  SEARCH_CATEGORIES,
  SEARCH_CATEGORIES_INTERNSHIPS,
} from '@backrs_ui/grow-ui/src/pages/Search/search.filters';
import { SearchCategory } from '@backrs_ui/grow-ui/src/pages/Search/search.types';

import {
  SEARCH_PARENT_CATEGORIES_DEFAULT,
  SEARCH_PARENT_CATEGORIES_INTERNSHIPS,
} from '../search.config';

/**
 * Custom hook to generate dynamic internship categories based on industries and career paths coming from the BE
 */
export const useDynamicInternshipCategories = (
  industries: Industry[] | undefined,
  careerCategories: CareerCategory[] | undefined,
  careerPaths: CareerPath[] | undefined,
) => {
  return useMemo(() => {
    // Create a copy of the original categories
    const internshipCategories = { ...SEARCH_PARENT_CATEGORIES_INTERNSHIPS };

    if (industries && industries.length > 0) {
      internshipCategories.INDUSTRY = {
        name: 'Industry',
        iconName: 'Building01',
        categories: [
          {
            key: SEARCH_CATEGORIES_INTERNSHIPS.INDUSTRY_ALL,
            label: 'All',
            clearsOtherFilters: true,
          },
          ...industries
            .slice()
            .sort((a, b) => a.name.localeCompare(b.name))
            .map((industry) => ({
              key: `INTERNSHIP_INDUSTRY_${industry.id}` as SearchCategory,
              label: industry.name,
            })),
        ],
        isInMoreFilters: false,
      };
    }

    // If we have career paths from the state, create a dynamic CAREER_PATH category
    if (careerPaths && careerPaths.length > 0) {
      const careerPathsGroupedByCategory: Record<CareerCategory['id'], CareerPath[]> =
        careerPaths.reduce(
          (acc, path) => {
            const catId = path.career_category_id;

            if (!acc[catId]) {
              acc[catId] = [];
            }

            return {
              ...acc,
              [catId]: [...acc[catId], path],
            };
          },
          {} as Record<number, CareerPath[]>,
        );

      internshipCategories.CAREER_PATH = {
        name: 'Career Path',
        iconName: 'Luggage02',
        categories: [
          {
            key: SEARCH_CATEGORIES_INTERNSHIPS.CAREER_PATH_ALL,
            label: 'All',
            clearsOtherFilters: true,
          },
          ...Object.entries(careerPathsGroupedByCategory).map(([catId, paths]) => {
            const careerCategory = careerCategories?.find((cat) => cat.id === parseInt(catId));

            return {
              key: `INTERNSHIP_CAREER_PATH_CATEGORY_${catId}` as SearchCategory,
              label: careerCategory?.name || '',
              isParent: true,
              subCategories: paths.map((path) => ({
                key: `INTERNSHIP_CAREER_PATH_${path.id}` as SearchCategory,
                label: path.name,
              })),
            };
          }),
        ],
        isInMoreFilters: false,
      };
    }

    return Object.values(internshipCategories);
  }, [careerPaths, industries, careerCategories]);
};

export const useDynamicDefaultCareerPathCategories = (
  // careerCategories: CareerCategory[] | undefined,
  careerPaths: CareerPath[] | undefined,
) => {
  return useMemo(() => {
    // Create a new object with CAREER_OPPORTUNITIES as the first key
    const defaultBackrsCategories = {} as typeof SEARCH_PARENT_CATEGORIES_DEFAULT;

    // Set up CAREER_OPPORTUNITIES first (whether we have career paths or not)
    defaultBackrsCategories.CAREER_OPPORTUNITIES =
      careerPaths && careerPaths.length > 0
        ? {
            name: 'Opportunities',
            iconName: 'Luggage02',
            isInMoreFilters: false,
            categories: [
              { key: SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_INTERNSHIPS, label: 'Internships' },
              { key: SEARCH_CATEGORIES.CAREER_OPPORTUNITIES_JOBS, label: 'Jobs' },
              {
                key: SEARCH_CATEGORIES_INTERNSHIPS.CAREER_PATH_ALL,
                label: 'Career Paths',
                clearsOtherFilters: true,
                isParent: true,
                subCategories: [
                  {
                    key: SEARCH_CATEGORIES_INTERNSHIPS.CAREER_PATH_ALL,
                    label: 'All',
                    clearsOtherFilters: true,
                  },
                  ...careerPaths
                    .map((path) => ({
                      key: `INTERNSHIP_CAREER_PATH_${path.id}` as SearchCategory,
                      label: path.name,
                    }))
                    .sort((a, b) => a.label.localeCompare(b.label)),
                ],
              },
            ],
          }
        : SEARCH_PARENT_CATEGORIES_DEFAULT.CAREER_OPPORTUNITIES;

    // Add the rest of the keys from the original object
    Object.entries(SEARCH_PARENT_CATEGORIES_DEFAULT).forEach(([key, value]) => {
      if (key !== 'CAREER_OPPORTUNITIES') {
        defaultBackrsCategories[key as keyof typeof SEARCH_PARENT_CATEGORIES_DEFAULT] = value;
      }
    });

    return Object.values(defaultBackrsCategories);
  }, [careerPaths]);
};
